import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

/**
 * Durable persistence backstop for native (iOS/Android).
 *
 * Why this exists
 * ---------------
 * All non-secret app state — auth context (`paas_tenant_id`,
 * `paas_regional_api_url`, `paas_user`, `paas_access_token`), the SSO community
 * list (`paas_sso_tenants`), the active community domain
 * (`paas_active_portal_domain`), branding (`paas_branding` / `paas_base_branding`)
 * and cached module/experience config — is written to **WKWebView
 * localStorage**. Only the refresh + SSO session tokens live in the Keychain
 * (via `secureStorage`).
 *
 * On iOS the app is served from a custom scheme origin (`ip4cms://localhost`).
 * WKWebView localStorage under that origin is NOT reliably persisted across an
 * app kill / OS data eviction / reinstall, while the Keychain is. The observable
 * failure: after reopening the SSO app the user is still authenticated (Keychain
 * survived) but the Communities nav is empty and the community branding has
 * reverted to the hub — because `linkedTenants`, `active_portal_domain`, etc.
 * were silently dropped, so bootstrap re-resolves the configured hub domain.
 *
 * Fix
 * ---
 * Mirror every `paas_*` localStorage write into Capacitor Preferences
 * (UserDefaults / SharedPreferences — durable across kill AND reinstall), and at
 * boot seed localStorage back from Preferences before the stores hydrate. Web is
 * untouched (localStorage there is already durable per-origin).
 */

const PREFIX = 'paas_';
let mirrorInstalled = false;

const isNative = (): boolean => Capacitor.isNativePlatform();

/**
 * Restore any `paas_*` keys that WKWebView localStorage dropped from the durable
 * Preferences mirror. Must run before the Zustand stores read localStorage
 * (authStore.hydrate, brandingStore._hydrate). Never clobbers a value already
 * present in localStorage (an in-session write is always fresher). No-op on web.
 */
export async function seedLocalStorageFromPrefs(): Promise<void> {
  if (!isNative()) {
    console.info('[durable] seed skipped (web)');
    return;
  }
  try {
    const { keys } = await Preferences.keys();
    const ours = keys.filter((k) => k.startsWith(PREFIX));
    let seeded = 0;
    const lsBefore = ourLocalStorageKeys();
    await Promise.all(
      ours.map(async (key) => {
        try {
          if (localStorage.getItem(key) != null) return;
          const { value } = await Preferences.get({ key });
          if (value != null) {
            localStorage.setItem(key, value);
            seeded += 1;
          }
        } catch {
          /* per-key best effort */
        }
      }),
    );
    console.info(
      `[durable] seed: prefs has ${ours.length} paas_* keys [${ours.join(',')}]; ` +
        `localStorage had ${lsBefore.length} [${lsBefore.join(',')}]; restored ${seeded}`,
    );
  } catch (error) {
    console.warn('[durable] seed from preferences failed:', error);
  }
}

/** Snapshot of our app's localStorage keys — diagnostics only. */
function ourLocalStorageKeys(): string[] {
  const out: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) out.push(k);
    }
  } catch { /* noop */ }
  return out;
}

/**
 * Write-through mirror: patch localStorage so every `paas_*` set/remove is also
 * applied to Capacitor Preferences. Idempotent; no-op on web. Install once at
 * boot, before any store writes, so the whole session is mirrored without having
 * to touch each store.
 */
export function installDurableMirror(): void {
  if (mirrorInstalled || !isNative()) return;
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const ls = window.localStorage;
    const origSet = ls.setItem.bind(ls);
    const origRemove = ls.removeItem.bind(ls);

    ls.setItem = (key: string, value: string): void => {
      origSet(key, value);
      if (typeof key === 'string' && key.startsWith(PREFIX)) {
        void Preferences.set({ key, value }).catch(() => { /* best effort */ });
      }
    };
    ls.removeItem = (key: string): void => {
      origRemove(key);
      if (typeof key === 'string' && key.startsWith(PREFIX)) {
        void Preferences.remove({ key }).catch(() => { /* best effort */ });
      }
    };
    mirrorInstalled = true;
    console.info('[durable] write-through mirror installed (native)');
  } catch (error) {
    console.warn('[durable] install mirror failed:', error);
  }
}
